import { isDataView } from 'util/types'
import { LocationEnum, QuestType, Quests, QuestChapter } from '../types'
import { isDemo } from '@/utils'
import { STOKE_CAP_TIERS } from './stoke'

export const QUESTS: Quests = {
  the_surf_lesson: {
    key: 'the_surf_lesson',
    name: 'The Surf Lesson',
    locationIndex: 1,
    chapterIndex: 1,
    location: 'mediterranean' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Enzo gets his first surf lesson and learns the basics of surfing.',
    effect: {
      skillsUnlocked: ['whitewater_riding', 'fitness', 'paddling', 'popup'],
      upgradePurchased: 'board_70oldmal'
    },
    unlockRequirements: [{ category: 'purchase', key: 'first_surf_lesson', value: 1 }],
    unlock: 'Completing your first surf lesson.',
    story:
      "Enzo's 20th birthday gift is a surf lesson at Marina di Pietrasanta. His instructor Marco teaches him the basics—popping up, riding whitewater, and dealing with wipeouts."
  },
  the_first_taste_of_saltwater: {
    key: 'the_first_taste_of_saltwater',
    name: 'The First Taste of Saltwater',
    locationIndex: 2,
    chapterIndex: 1,
    location: 'mediterranean' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks the Marina di Pietrasanta break (+10% to popup and whitewater riding).',
    effect: {
      surfSpotUnlocked: 'Marina di Pietrasanta',
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'popup', value: 1.3 },
        { type: 'permanent', effectCategory: 'skill', key: 'paddling', value: 1.2 },
        { type: 'permanent', effectCategory: 'skill', key: 'whitewater_riding', value: 1.3 }
      ]
    },
    unlockRequirements: [{ category: 'local_stat', key: 'wavesTotal', value: 10 }],
    unlock: 'Riding your first surf.',
    story:
      "Enzo falls hard, but the thrill of riding his first small wave leaves him eager for more. Marco sees Enzo's stoke from surfing and decides to give him an old beaten surfobard from 70's to keep."
  },
  first_steps_to_independence: {
    key: 'first_steps_to_independence',
    name: 'First Steps to Independence',
    locationIndex: 3,
    chapterIndex: 1,
    location: 'mediterranean' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Capo Mannu (+10% to paddling power and balance).',
    effect: {
      surfSpotUnlocked: 'Capo Mannu',
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'paddling', value: 1.4 },
        { type: 'permanent', effectCategory: 'skill', key: 'popup', value: 1.3 }
      ]
    },
    unlockRequirements: [
      { category: 'job', key: 'dishwasher', value: 20 },
      { category: 'currency', key: 'money', value: 100 }
    ],
    unlock: 'Save enough money to buy your own surfboard.',
    story:
      'Enzo wants his own board but lacks the money. He starts working at a local café as a Dishwasher, slowly saving for his first surfboard. Marco continues to support him as he practices in his free time, slowly improving his paddling and balance.'
  },
  lessons_from_the_locals: {
    key: 'lessons_from_the_locals',
    name: 'Lessons from the Locals',
    locationIndex: 4,
    chapterIndex: 1,
    location: 'mediterranean' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Varazze (+15% to paddling power and balance).',
    effect: {
      surfSpotUnlocked: 'Varazze',
      skillsUnlocked: ['green_wave_riding', 'wave_reading'],
      multipliers: [
        { type: 'permanent', effectCategory: 'currency', key: 'crit_probability', value: 1.5 },
        { type: 'permanent', effectCategory: 'currency', key: 'crit_multiplier', value: 1.4 }
      ]
    },
    unlockRequirements: [
      { category: 'job', key: 'street_vendor_helper', value: 30 },
      { category: 'local_stat', key: 'wavesTotal', value: 50 }
    ],
    unlock: 'Earn 20 respect points by helping locals.',
    story:
      "As Enzo spends more time at the beach, he starts working part-time as a Street Vendor Helper. Locals like him for his willingness to help, but they're not impressed by his surfing yet. He earns respect by helping tourists and learning about the ocean from older local surfers who tell him how to read waves and to travel to Varazze where he can finally learn to surf green waves."
  },
  the_mediterranean_farewell: {
    key: 'the_mediterranean_farewell',
    name: 'The Mediterranean Farewell',
    locationIndex: 5,
    chapterIndex: 1,
    location: 'mediterranean',
    type: 'main' as QuestType,
    effectText: 'Unlocks the ability to travel to France.',
    effect: {
      locationUnlocked: 'france',
      surfSpotUnlocked: 'Hossegor',
      automationUnlocked: 'work'
    },
    unlockRequirements: [
      { category: 'currency', key: 'money', value: 300 },
      { category: 'local_stat', key: 'wavesTotal', value: 100 }
    ],
    unlock: 'Save $500, earn 3 respect points.',
    story:
      "Marco encourages Enzo to head to France if he wants to improve his surfing. With more consistent and bigger waves, France offers more opportunities for growth. Enzo spends a few more weeks working at a café, saving just enough to make the journey. He leaves the Mediterranean behind, excited but nervous for what's ahead."
  },
  // Chapter 2: France
  the_challenge_of_hossegor: {
    key: 'the_challenge_of_hossegor',
    name: 'The Challenge of Hossegor',
    locationIndex: 1,
    chapterIndex: 2,
    location: 'france' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Hossegor (+15% to bottom turns).',
    effect: {
      skillsUnlocked: ['bottom_turn_front'],
      surfSpotUnlocked: 'Biarritz',
      multipliers: [{ type: 'permanent', effectCategory: 'skill', key: 'bottom_turn_front', value: 1.45 }]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'wavesTotal', value: 40 },
      { category: 'local_stat', key: 'wipeoutsTotal', value: 15 }
    ],
    unlock: 'Successfully ride 20 waves at Hossegor.',
    story:
      "Enzo's arrival in Hossegor is a humbling experience. The waves are faster and more powerful than anything he's faced. He meets Thierry, a local surf guide who offers advice, but Jacques, a competitive surfer, mocks him. Enzo struggles but rides his first powerful waves at Hossegor, gaining confidence despite Jacques' antagonism."
  },
  broke_in_biarritz: {
    key: 'broke_in_biarritz',
    name: 'Broke in Biarritz',
    locationIndex: 2,
    chapterIndex: 2,
    location: 'france' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Biarritz (+10% to wave reading and forecasting).',
    effect: {
      skillsUnlocked: ['forecasting_and_observing'],
      surfSpotUnlocked: 'La Gravière',
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'forecasting_and_observing', value: 1.3 },
        { type: 'permanent', effectCategory: 'skill', key: 'wave_reading', value: 1.3 }
      ]
    },
    unlockRequirements: [
      { category: 'job', key: 'surfboard_rental_assistant', value: 30 },
      { category: 'local_stat', key: 'gameDaysTotal', value: 30 }
    ],
    unlock: 'Work 20 hours as a Tour Guide Helper.',
    story:
      "Enzo runs out of money in Biarritz, a tourist-heavy surf town. He meets Sophie, a friendly local who offers him a job as a Tour Guide Helper. Sophie helps him understand the tides and conditions, and Enzo slowly learns to predict perfect surf days. There's no villain here—just the harsh reality of needing money to survive."
  },
  rivalry_at_la_graviere: {
    key: 'rivalry_at_la_graviere',
    name: 'Rivalry at La Gravière',
    locationIndex: 3,
    chapterIndex: 2,
    location: 'france' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks La Gravière (+20% to fitness and paddling power).',
    effect: {
      skillsUnlocked: ['bottom_turn_back'],
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'fitness', value: 1.6 },
        { type: 'permanent', effectCategory: 'skill', key: 'paddling', value: 1.6 },
        { type: 'permanent', effectCategory: 'skill', key: 'bottom_turn_back', value: 1.45 }
      ]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'wavesTotal', value: 100 },
      { category: 'local_stat', key: 'wipeoutsTotal', value: 30 },
      { category: 'local_stat', key: 'respectTotal', value: 20 }
    ],
    unlock: 'Survive a session at La Gravière on a medium swell.',
    story:
      "Jacques continues to taunt Enzo, claiming he'll never be able to handle the heavy barrels at La Gravière. Determined to prove Jacques wrong, Enzo trains with Thierry and Sophie to build his fitness and paddling power. The surf-off at La Gravière is grueling, but Enzo finally earns Jacques' reluctant respect after beating him in the challenge."
  },
  the_journey_continues: {
    key: 'the_journey_continues',
    name: 'The Journey Continues',
    locationIndex: 4,
    chapterIndex: 2,
    location: 'france' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks the ability to travel to Portugal.',
    effect: {
      surfSpotUnlocked: 'Peniche',
      locationUnlocked: 'portugal' as LocationEnum
    },
    unlockRequirements: [
      { category: 'currency', key: 'money', value: 500 },
      { category: 'local_stat', key: 'wavesTotal', value: 200 },
      { category: 'job', key: 'beach_lifeguard_assistant', value: 100 },
      { category: 'local_stat', key: 'wipeoutsTotal', value: 50 }
    ],
    unlock: 'Save $500, earn 100 respect points.',
    story:
      'After proving himself in France, Enzo is ready to move on. Sophie, now a close friend, encourages him to head to Portugal. With bigger waves and cheaper living costs, it\'s the perfect place for Enzo to continue improving. Jacques, now grudgingly respectful, gives Enzo some parting advice: "Portugal\'s waves are a different beast—be careful."'
  },

  // Chapter 3: Portugal
  peniches_shifting_sands: {
    key: 'peniches_shifting_sands',
    name: "Peniche's Shifting Sands",
    locationIndex: 1,
    chapterIndex: 3,
    location: 'portugal' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Peniche (+20% to turns and cutbacks).',
    effect: {
      surfSpotUnlocked: 'Peniche',
      skillsUnlocked: ['cutback_back'],
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'cutback_back', value: 1.6 },
        { type: 'permanent', effectCategory: 'currency', key: 'crit_probability', value: 1.3 },
        { type: 'permanent', effectCategory: 'currency', key: 'crit_multiplier', value: 1.22 }
      ]
    },
    unlockRequirements: [
      { category: 'level', key: 'bottom_turn_front', value: 2 },
      { category: 'local_stat', key: 'criticalMovesTotal', value: 10 }
    ],
    unlock: 'Successfully ride 50 waves at Peniche.',
    story:
      "Enzo arrives in Peniche, known for its ever-shifting sandbanks. He meets Thiago, a local surf instructor, who helps Enzo understand the importance of adaptability in these unpredictable waters. Peniche tests Enzo's ability to stay flexible, both physically and mentally, as the waves constantly shift."
  },
  facing_nazares_fury: {
    key: 'facing_nazares_fury',
    name: "Facing Nazaré's Fury",
    locationIndex: 2,
    chapterIndex: 3,
    location: 'portugal' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Nazaré (+25% to barrel riding, +15% to paddling power).',
    effect: {
      surfSpotUnlocked: 'Nazaré',
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'popup', value: 1.75 },
        { type: 'permanent', effectCategory: 'skill', key: 'paddling', value: 1.45 },
        { type: 'permanent', effectCategory: 'skill', key: 'fitness', value: 1.5 }
      ]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'surfSessionsTotal', value: 30 },
      { category: 'local_stat', key: 'wipeoutsTotal', value: 15 }
    ],
    unlock: 'Survive a session at Nazaré on a medium swell.',
    story:
      "Enzo is warned by Thiago that Nazaré is not for beginners, but his curiosity gets the better of him. On a medium swell day, Enzo paddles out with Thiago's guidance. The waves are monstrous, and Enzo barely survives the session. It's a humbling experience, showing him just how much he still has to learn."
  },
  the_mysterious_traveler: {
    key: 'the_mysterious_traveler',
    name: 'The Mysterious Traveler',
    locationIndex: 3,
    chapterIndex: 3,
    location: 'portugal' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Ericeira (+15% to wave reading and forecasting).',
    effect: {
      surfSpotUnlocked: 'Ericeira',
      skillsUnlocked: ['cutback_front'],
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'forecasting_and_observing', value: 1.5 },
        { type: 'permanent', effectCategory: 'skill', key: 'cutback_front', value: 1.6 },
        { type: 'permanent', effectCategory: 'skill', key: 'wave_reading', value: 1.6 }
      ]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'wavesTotal', value: 300 },
      { category: 'level', key: 'skill', value: 3 }
    ],
    unlock: 'Learn new wave-reading techniques from Sebastião.',
    story:
      'Enzo meets Sebastião, an enigmatic older surfer who lives a minimalist life and works as a surf guide. Sebastião teaches Enzo few tricks and promises that if he skills up, he can help him to start a new surf guiding career.'
  },
  a_leap_of_faith: {
    key: 'a_leap_of_faith',
    name: 'A Leap of Faith',
    locationIndex: 4,
    chapterIndex: 3,
    location: 'portugal' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks the ability to travel to South Africa.',
    effect: {
      locationUnlocked: 'south_africa' as LocationEnum,
      surfSpotUnlocked: 'Jeffreys Bay',
      automationUnlocked: 'surf',
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'bottom_turn_front', value: 1.6 },
        { type: 'permanent', effectCategory: 'skill', key: 'bottom_turn_back', value: 1.6 },
        { type: 'permanent', effectCategory: 'skill', key: 'cutback_front', value: 1.4 },
        { type: 'permanent', effectCategory: 'skill', key: 'cutback_back', value: 1.4 }
      ]
    },
    unlockRequirements: [
      { category: 'currency', key: 'money', value: 2000 },
      { category: 'local_stat', key: 'respectTotal', value: 100 },
      { category: 'job', key: 'surf_camp_organizer', value: 60 }
    ],
    unlock: 'Save $800, earn 100 respect points.',
    story:
      "After several months of hard work and significant surf improvement in Portugal, Sebastião reveals he's leaving for South Africa. He tells Enzo about his friend who runs a shark diving business and is specifically looking for someone who speaks Italian. 'Plus,' Sebastião adds with a knowing smile, 'the waves there are world-class. Perfect for taking your surfing to the next level.' This time the decision feels heavier, but Enzo decides to join him on the journey."
  },

  // Chapter 4: South Africa
  teaching_italian_to_shark_diver: {
    key: 'teaching_italian_to_shark_diver',
    name: 'Teaching Italian to Shark Diver',
    locationIndex: 1,
    chapterIndex: 4,
    location: 'south_africa' as LocationEnum,
    type: 'main' as QuestType,
    effectText: '+25% to wave reading and forecasting skills.',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'forecasting_and_observing', value: 1.8 },
        { type: 'permanent', effectCategory: 'skill', key: 'wave_reading', value: 1.8 }
      ]
    },
    unlockRequirements: [{ category: 'job', key: 'teaching_italian', value: 80 }],
    unlock: 'Work 80 hours teaching Italian to shark diving tourists.',
    story:
      "Enzo begins working with Thabo's shark diving business, teaching Italian to the guides and helping translate for Italian tourists. During boat trips to shark sites, Enzo learns valuable lessons about reading ocean conditions from the experienced boat captains. They teach him how to spot currents, read weather patterns, and understand how these factors affect wave formation. 'Sharks and waves aren't so different,' Thabo tells him. 'Both demand respect and careful observation.' This experience not only helps Enzo earn money but also significantly improves his ability to read the ocean—a skill that will prove invaluable for his surfing journey in South Africa's challenging waters."
  },

  the_call_of_jeffreys_bay: {
    key: 'the_call_of_jeffreys_bay',
    name: 'The Call of Jeffreys Bay',
    locationIndex: 2,
    chapterIndex: 4,
    location: 'south_africa' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Jeffreys Bay (+25% to bottom turns and cutbacks).',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'bottom_turn_front', value: 1.4 },
        { type: 'permanent', effectCategory: 'skill', key: 'bottom_turn_back', value: 1.4 },
        { type: 'permanent', effectCategory: 'skill', key: 'cutback_front', value: 1.6 },
        { type: 'permanent', effectCategory: 'skill', key: 'cutback_back', value: 1.25 }
      ]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'wavesTotal', value: 300 },
      { category: 'local_stat', key: 'wipeoutsTotal', value: 30 }
    ],
    unlock: 'Successfully ride 50 waves at Jeffreys Bay.',
    story:
      "Enzo arrives in Jeffreys Bay, one of the most iconic surf spots in the world. The waves here move faster than anything he's ridden before, and his first few sessions are humbling. The locals don't take kindly to outsiders at first, but with patience and persistence, Enzo begins to prove himself. He learns to position himself better, time his paddles right, and control his board at higher speeds. Slowly, he starts unlocking his potential here, earning nods of approval from the local surfers."
  },
  conquering_fear: {
    key: 'conquering_fear',
    name: 'Conquering Fear',
    locationIndex: 3,
    chapterIndex: 4,
    location: 'south_africa' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Cape St. Francis (+15% to paddling power, +10% to barrel riding).',
    effect: {
      surfSpotUnlocked: 'Cape St. Francis',
      skillsUnlocked: ['speed_generation'],
      multipliers: [
        { type: 'permanent', effectCategory: 'currency', key: 'crit_probability', value: 1.25 },
        { type: 'permanent', effectCategory: 'currency', key: 'crit_multiplier', value: 1.3 },
        { type: 'permanent', effectCategory: 'skill', key: 'speed_generation', value: 1.4 }
      ]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'wavesTotal', value: 500 },
      { category: 'local_stat', key: 'criticalMovesTotal', value: 50 }
    ],
    unlock: 'Face your fear of heavy waves and succeed at Cape St. Francis.',
    story:
      "Cape St. Francis is notorious for its powerful waves, and Enzo is terrified. His first few attempts end in brutal wipeouts, leaving him shaken. He begins to wonder if he's reached his limit. But then he meets Thandi, a local surfer who teaches him a simple truth: fear is part of surfing. With her encouragement, he paddles back out, determined to push through the fear. With each attempt, he learns to trust his instincts more. Finally, one afternoon, he commits, drops into a fast-moving wave, and holds his line. The thrill outweighs the fear. He's done it—he's conquered Cape St. Francis."
  },
  the_long_paddle_to_durban: {
    key: 'the_long_paddle_to_durban',
    name: 'The Long Paddle to Durban',
    locationIndex: 4,
    chapterIndex: 4,
    location: 'south_africa' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Durban (+20% to fitness and paddling power).',
    effect: {
      surfSpotUnlocked: 'Durban',
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'fitness', value: 1.9 },
        { type: 'permanent', effectCategory: 'skill', key: 'paddling', value: 1.8 }
      ]
    },
    unlockRequirements: [{ category: 'level', key: 'paddling', value: 4 }],
    unlock: 'Complete a long-distance paddle session along the Durban coast.',
    story:
      "Enzo's fitness is put to the test when a group of surfers in Durban invite him to join a long-distance paddle challenge. The session is grueling, pushing him to his limits. His arms burn, his lungs ache, and for the first time in a long time, he questions if he can finish. But as he paddles alongside them, he feels a shift. He's not just surfing anymore—he's proving something to himself. By the end, when they finally reach shore, the Durban surfers nod in respect. Enzo has built not only strength but also a reputation."
  },
  the_soul_of_south_africa: {
    key: 'the_soul_of_south_africa',
    name: 'The Soul of South Africa',
    locationIndex: 5,
    chapterIndex: 4,
    location: 'south_africa' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks the ability to travel to California.',
    effect: {
      locationUnlocked: 'california' as LocationEnum,
      surfSpotUnlocked: 'Huntington Beach',
      skillsUnlocked: ['top_turn_front'],
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'speed_control', value: 1.45 },
        { type: 'permanent', effectCategory: 'skill', key: 'bottom_turn_front', value: 1.45 },
        { type: 'permanent', effectCategory: 'skill', key: 'bottom_turn_back', value: 1.45 }
      ]
    },
    unlockRequirements: [
      { category: 'currency', key: 'money', value: 10000 },
      { category: 'local_stat', key: 'respectTotal', value: 400 },
      { category: 'job', key: 'safari_surf_tour_operator', value: 200 }
    ],
    unlock: 'Save $10000, earn 400 respect points in South Africa.',
    story:
      "After facing the challenges of Jeffreys Bay and Cape St. Francis, Enzo feels a deep connection to the raw power of the ocean. His time in South Africa has transformed him, giving him both the physical and mental strength to take on bigger waves. Locals tell him about the legendary breaks of California, and he decides it's time to move westward. With savings and countless lessons learned, Enzo books his flight, eager to chase the next horizon."
  },

  // Chapter 5: California
  the_california_dream: {
    key: 'the_california_dream',
    name: 'The California Dream',
    locationIndex: 1,
    chapterIndex: 5,
    location: 'california' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Huntington Beach (+15% to bottom turns and paddling power).',
    effect: {
      surfSpotUnlocked: 'Huntington Beach',
      skillsUnlocked: ['top_turn_back'],
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'bottom_turn_front', value: 1.4 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_front', value: 1.7 }
      ]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'wavesTotal', value: 900 },
      { category: 'local_stat', key: 'criticalMovesTotal', value: 180 }
    ],
    unlock: 'Successfully ride 500 waves at Huntington Beach.',
    story:
      'Enzo arrives in California wide-eyed, inspired by the legendary surf culture. He meets Cody, a hardcore local who thrives on cold water waves. Cody shows Enzo around and helps him understand the rhythm of the beach breaks at Huntington Beach. But the real challenge is adapting to the fast, competitive California scene. Enzo struggles to keep up but learns the importance of perseverance.'
  },
  a_cold_reality: {
    key: 'a_cold_reality',
    name: 'A Cold Reality',
    locationIndex: 2,
    chapterIndex: 5,
    location: 'california' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Malibu (+10% to wave reading and speed control).',
    effect: {
      surfSpotUnlocked: 'Malibu',
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'bottom_turn_back', value: 1.4 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_back', value: 1.7 }
      ]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'criticalMovesTotal', value: 250 },
      { category: 'job', key: 'surf_school_assistant', value: 180 }
    ],
    unlock: 'Ride 40 waves and earn 60 respect points at Malibu.',
    story:
      "Enzo's next destination is Malibu, where the long, slow waves challenge his patience and technique. Cody, his friend from Huntington Beach, offers him a job as a surf coach assistant at a local surf school. 'The pay isn't great, but you'll get to surf the best waves during off-hours,' Cody explains. While teaching beginners, Enzo refines his own understanding of wave reading and flow. The slower pace of Malibu forces him to be more deliberate with his movements, and through teaching others, he discovers that surfing isn't just about competition—it's about connecting with the ocean and sharing that passion with others."
  },
  first_competition_at_huntington: {
    key: 'first_competition_at_huntington',
    name: 'First Competition at Huntington Beach',
    locationIndex: 3,
    chapterIndex: 5,
    location: 'california' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Trestles (+20% to fitness and top turns).',
    effect: {
      surfSpotUnlocked: 'Trestles',
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'fitness', value: 1.6 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_front', value: 1.6 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_back', value: 1.6 }
      ]
    },
    unlockRequirements: [{ category: 'contest', key: 'huntington_beach_open', value: 'round_2' }],
    unlock: 'Place in the bottom half of a Trestles local contest.',
    story:
      "Enzo enters his first real contest at Huntington Beach, but he's unprepared for the level of skill he faces. Despite trying his best, Enzo finishes near the bottom of the rankings, a humbling experience. However, Cody reminds him that every pro starts at the bottom and that California competitions are notoriously tough. Enzo leaves with a renewed sense of determination and a deeper understanding of his weaknesses."
  },
  chasing_warmer_waters: {
    key: 'chasing_warmer_waters',
    name: 'Chasing Warmer Waters',
    locationIndex: 4,
    chapterIndex: 5,
    location: 'california' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks the ability to travel to Australia East Coast.',
    effect: {
      locationUnlocked: 'australia_east_coast' as LocationEnum,
      surfSpotUnlocked: 'Byron Bay'
    },
    unlockRequirements: [
      { category: 'currency', key: 'money', value: 18000 },
      { category: 'local_stat', key: 'respectTotal', value: 200 },
      { category: 'job', key: 'beach_manager', value: 240 }
    ],
    unlock: 'Save $1200, earn 100 respect points in California.',
    story:
      "As Enzo struggles through cold California waters, Cody starts dreaming of warmer, more diverse surf breaks. Together, they decide to travel to Australia's East Coast, where the weather is warmer and the waves are world-class. Enzo sees Australia as his next step in improving his skills and moving closer to true competition, knowing that it will offer even more challenges."
  },

  // Chapter 6: Australia East Coast
  byron_bays_calm_before_the_storm: {
    key: 'byron_bays_calm_before_the_storm',
    name: "Byron Bay's Calm Before the Storm",
    locationIndex: 1,
    chapterIndex: 6,
    location: 'australia_east_coast' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Byron Bay (+20% to balance and paddling power).',
    effect: {
      skillsUnlocked: ['speed_control'],
      surfSpotUnlocked: 'Snapper Rocks',
      multipliers: [{ type: 'permanent', effectCategory: 'skill', key: 'speed_control', value: 1.6 }]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'surfSessionsTotal', value: 40 },
      { category: 'job', key: 'surfboard_shaping_apprentice', value: 160 }
    ],
    unlock: 'Successfully ride 50 waves at Byron Bay.',
    story:
      'Enzo and Cody arrive in Byron Bay, where the vibe is relaxed, and the waves are gentle but long. They meet and become friends with Toby, a laid-back local who works for a surfboard shaper. Running low on funds, Enzo and Cody need money to continue their journey. Toby offers them jobs as surf shaper apprentices and introduces Enzo to the importance of speed control in slow rolling waves.'
  },
  snapper_rocks_fury: {
    key: 'snapper_rocks_fury',
    name: "Snapper Rocks' Fury",
    locationIndex: 2,
    chapterIndex: 6,
    location: 'australia_east_coast' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Snapper Rocks (+20% to bottom turns and speed generation).',
    effect: {
      skillsUnlocked: ['floater'],
      surfSpotUnlocked: 'Gold Coast',
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'floater', value: 1.6 },
        { type: 'permanent', effectCategory: 'skill', key: 'speed_generation', value: 1.6 }
      ]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'wavesTotal', value: 1000 },
      { category: 'local_stat', key: 'wipeoutsTotal', value: 80 }
    ],
    unlock: 'Ride 40 waves at Snapper Rocks and survive the powerful breaks.',
    story:
      "Enzo heads to Snapper Rocks, where the waves are fast, and the lineup is fierce. He struggles with the powerful right-handers and heavy currents, constantly getting caught in bad positions. Then, he meets Mick, the Australian surfing legend, who encourages him to adapt to the break's aggressive nature and numerous crowds."
  },
  battling_through_the_gold_coast: {
    key: 'battling_through_the_gold_coast',
    name: 'Battling Through the Gold Coast',
    locationIndex: 3,
    chapterIndex: 6,
    location: 'australia_east_coast' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Gold Coast (+15% to fitness, +10% to turns).',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'fitness', value: 1.15 },
        { type: 'permanent', effectCategory: 'skill', key: 'cutback_front', value: 1.1 },
        { type: 'permanent', effectCategory: 'skill', key: 'cutback_back', value: 1.1 }
      ]
    },
    unlockRequirements: [{ category: 'contest', key: 'gold_coast_open', value: 'round_3' }],
    unlock: 'Place in the top half of a Gold Coast local contest.',
    story:
      "Enzo enters a contest at Gold Coast, eager to see where he stands. The competition is fierce, and he struggles against the locals, finishing in the middle of the rankings. It's a wake-up call—he's improved, but he's still not among the best. Determined, he keeps training, focusing on his turns and fitness to prepare for the next challenge."
  },
  the_warm_australian_goodbye: {
    key: 'the_warm_australian_goodbye',
    name: 'The Warm Australian Goodbye',
    locationIndex: 4,
    chapterIndex: 6,
    location: 'australia_east_coast' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks the ability to travel to Indonesia.',
    effect: {
      locationUnlocked: 'indonesia' as LocationEnum,
      surfSpotUnlocked: 'Uluwatu',
      multipliers: [{ type: 'permanent', effectCategory: 'skill', key: 'floater', value: 1.7 }]
    },
    unlockRequirements: [
      { category: 'currency', key: 'money', value: 10000 },
      { category: 'local_stat', key: 'respectTotal', value: 250 },
      { category: 'job', key: 'surfboard_shaper', value: 160 }
    ],
    unlock: 'Save $1500, earn 150 respect points in Australia.',
    story:
      "After months of surfing and learning, Enzo decides it's time to leave Australia. With Cody heading back to cold waters, Enzo follows the call of Indonesia's legendary reef breaks. His friends in Australia encourage him to keep pushing, knowing that Indonesia will test his skills in ways he's never experienced before."
  },

  // Chapter 7: Indonesia
  uluwatus_relentless_waves: {
    key: 'uluwatus_relentless_waves',
    name: "Uluwatu's Relentless Waves",
    locationIndex: 1,
    chapterIndex: 7,
    location: 'indonesia' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Uluwatu (+20% to wave reading, +15% to paddling power).',
    effect: {
      surfSpotUnlocked: 'Padang Padang',
      skillsUnlocked: ['barrel'],
      multipliers: [{ type: 'permanent', effectCategory: 'skill', key: 'speed_control', value: 1.4 }]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'wavesTotal', value: 800 },
      { category: 'job', key: 'diving_instructor', value: 120 }
    ],
    unlock: 'Ride 50 waves at Uluwatu, survive 30 wipeouts, and work as a diving instructor.',
    story:
      "Enzo arrives at the famous Uluwatu, known for its powerful reef breaks and perfect barrels. He's determined to make it through his first barrel, but the waves are relentless. To fund his stay, he takes a job as a diving instructor, teaching tourists how to explore Indonesia's reefs. After several attempts at surfing, Enzo wipes out hard inside the tube, tumbling over the reef. Though bruised, he refuses to give up, gaining deeper respect for the ocean's power. Kadek, a local surfer, tells him, 'Sometimes the wave isn't ready for you yet.'"
  },
  the_frustration_of_padang_padang: {
    key: 'the_frustration_of_padang_padang',
    name: 'The Frustration of Padang Padang',
    locationIndex: 2,
    chapterIndex: 7,
    location: 'indonesia' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Padang Padang (+25% to barrel skills, +20% to wave reading).',
    effect: {
      surfSpotUnlocked: 'Mentawai Islands',
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'barrel', value: 1.5 },
        { type: 'permanent', effectCategory: 'skill', key: 'wave_reading', value: 1.4 }
      ]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'wavesTotal', value: 1200 },
      { category: 'level', key: 'barrel', value: 2 }
    ],
    unlock: 'Ride 150 waves at Padang Padang, survive 60 wipeouts, and work as a surf and yoga retreat manager.',
    story:
      "Padang Padang is a flawless barrel machine, but only for those who can handle it. Enzo spends days getting caught inside, unable to exit cleanly. Every time he gets close, the wave slams shut on him. His frustration grows, but local mentor Kadek reminds him, 'The wave doesn't owe you anything. You have to earn it.' Enzo refines his positioning and patience, slowly making progress."
  },
  mentawai_islands_the_first_barrel: {
    key: 'mentawai_islands_the_first_barrel',
    name: 'Mentawai Islands: The First Barrel',
    locationIndex: 3,
    chapterIndex: 7,
    location: 'indonesia' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Mentawai Islands (+30% to barrel skills, +20% to cutbacks).',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'barrel', value: 1.8 },
        { type: 'permanent', effectCategory: 'skill', key: 'speed_generation', value: 1.8 },
        { type: 'permanent', effectCategory: 'skill', key: 'speed_control', value: 1.4 }
      ]
    },
    unlockRequirements: [
      { category: 'level', key: 'barrel', value: 3 },
      { category: 'local_stat', key: 'wavesTotal', value: 1500 }
    ],
    unlock: 'Successfully ride your first complete barrel at the Mentawai Islands.',
    story:
      "The journey finally takes Enzo to the remote Mentawai Islands, known for their perfect, world-class waves. After a week of intense focus and countless attempts, Enzo catches a wave that lines up just right. He drops in, pulls into the barrel, and this time, everything clicks. The water rushes around him, and for the first time, Enzo makes it out cleanly. As he rides out of the tube, he feels an overwhelming sense of joy and satisfaction—this is the moment he's been chasing for so long. His first barrel is a memory that will stay with him forever, marking a huge milestone in his surf career."
  },
  the_next_horizon: {
    key: 'the_next_horizon',
    name: 'The Next Horizon',
    locationIndex: 4,
    chapterIndex: 7,
    location: 'indonesia' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks the ability to travel to Australia West Coast.',
    effect: {
      locationUnlocked: 'australia_west_coast' as LocationEnum,
      surfSpotUnlocked: 'North Point',
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'barrel', value: 1.4 },
        { type: 'permanent', effectCategory: 'skill', key: 'speed_control', value: 1.5 }
      ]
    },
    unlockRequirements: [
      { category: 'currency', key: 'money', value: 14000 },
      { category: 'local_stat', key: 'respectTotal', value: 350 },
      { category: 'contest', key: 'uluwatu_open', value: 'quarterfinals' }
    ],
    unlock: 'Save $8000, earn 200 respect points, and reach the quarter-finals in a local Bali contest.',
    story:
      "After months in Indonesia, Enzo has transformed. His barrel riding has improved dramatically, and he's gained respect in the lineup. When a local contest is announced, he decides to test his skills against other talented surfers. To his surprise, he advances through several rounds, eventually reaching the quarter-finals. Though he doesn't win, the experience ignites a new ambition. 'You have potential for the QS,' says one of the judges, referring to the World Surf Association Qualifying Series. 'You should try your luck in Western Australia.' With newfound confidence and purpose, Enzo sets his sights on the professional circuit and the challenging waves of Australia's west coast."
  },

  // Chapter 8: Australia West Coast - The QS Challenge
  entering_the_qs: {
    key: 'entering_the_qs',
    name: 'Entering the QS',
    locationIndex: 1,
    chapterIndex: 8,
    location: 'australia_west_coast' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks North Point (+25% to barrel skills, +20% to wave reading).',
    effect: {
      skillsUnlocked: ['aerial', 'air_reverse'],
      surfSpotUnlocked: 'Australian Wave Pool',
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_front', value: 1.6 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_back', value: 1.6 }
      ]
    },
    unlockRequirements: [
      { category: 'contest', key: 'north_point_open', value: 'quarterfinals' },
      { category: 'level', key: 'barrel', value: 4 }
    ],
    unlock: 'Reach quarter-finals in the North Point Open and achieve barrel level 3.',
    story:
      "Enzo arrives in Western Australia ready to test himself against true competition. He signs up for the North Point Open, his first real QS event. The waves are powerful and the competition fierce. As he advances through the rounds, he realizes his technique is solid, but he's missing something. In the quarter-finals, a young surfer from Brazil defeats him with a spectacular aerial maneuver. 'If you want to compete at this level,' says Jack, a veteran competitor, 'you need to add some air to your game.' Enzo knows what he needs to do next."
  },
  the_aerial_revolution: {
    key: 'the_aerial_revolution',
    name: 'The Aerial Revolution',
    locationIndex: 2,
    chapterIndex: 8,
    location: 'australia_west_coast' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Wave Pool (+30% to aerial and air reverse skills).',
    effect: {
      surfSpotUnlocked: 'Margaret River',
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'aerial', value: 2 },
        { type: 'permanent', effectCategory: 'skill', key: 'air_reverse', value: 2 },
        { type: 'permanent', effectCategory: 'skill', key: 'barrel', value: 1.2 }
      ]
    },
    unlockRequirements: [
      { category: 'level', key: 'aerial', value: 2 },
      { category: 'level', key: 'air_reverse', value: 2 },
      { category: 'local_stat', key: 'wavesTotal', value: 1000 }
    ],
    unlock: 'Reach aerial level 2, air reverse level 1, and ride 350 waves in the wave pool.',
    story:
      "Determined to evolve his surfing, Enzo heads to Australia's premier wave pool facility. 'This is where you'll learn aerials,' explains the coach. 'The waves are consistent and predictable—perfect for training.' Day after day, Enzo launches himself into the air, failing more often than landing. His body is battered from the constant impacts, but slowly, his aerial game improves. Each wave becomes a laboratory for his experimentation. 'You're getting it,' the coach says after a particularly impressive air reverse. 'Now you need to take it to the ocean.'"
  },
  margaret_river_qualification: {
    key: 'margaret_river_qualification',
    name: 'Margaret River Qualification',
    locationIndex: 3,
    chapterIndex: 8,
    location: 'australia_west_coast' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Margaret River (+25% to all skills).',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'aerial', value: 1.5 },
        { type: 'permanent', effectCategory: 'skill', key: 'air_reverse', value: 1.5 },
        { type: 'permanent', effectCategory: 'skill', key: 'barrel', value: 1.3 }
      ]
    },
    unlockRequirements: [
      { category: 'contest', key: 'wsa_qualifier_margaret_river', value: 'semifinals' },
      { category: 'level', key: 'aerial', value: 3 }
    ],
    unlock: 'Reach semi-finals in the Margaret River Pro and achieve aerial level 3.',
    story:
      "The Margaret River Pro is Enzo's biggest test yet. With his newly developed aerial skills and his experience in powerful waves, he feels ready for the challenge. The competition draws talent from around the world, all fighting for precious QS points. Enzo advances through the early rounds, employing a strategic mix of powerful turns, barrels, and calculated aerials. In the quarter-finals, he lands a perfect air reverse at a critical moment, earning one of the highest scores of the event. Though he's eventually stopped in the semi-finals, his performance has put him on the map. 'You've got a real shot at qualification,' his new sponsor representative tells him. 'Keep this up, and you'll be on the Championship Tour next year.'"
  },
  breakthrough_in_the_west: {
    key: 'breakthrough_in_the_west',
    name: 'Breakthrough in the West',
    locationIndex: 4,
    chapterIndex: 8,
    location: 'australia_west_coast' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks the ability to travel to Hawaii.',
    effect: {
      locationUnlocked: 'hawaii' as LocationEnum,
      surfSpotUnlocked: 'Pipeline',
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_front', value: 1.4 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_back', value: 1.4 },
        { type: 'permanent', effectCategory: 'skill', key: 'speed_generation', value: 1.5 },
        { type: 'permanent', effectCategory: 'skill', key: 'speed_control', value: 1.5 }
      ]
    },
    unlockRequirements: [
      { category: 'currency', key: 'money', value: 30000 },
      { category: 'local_stat', key: 'respectTotal', value: 450 },
      { category: 'level', key: 'aerial', value: 3 },
      { category: 'level', key: 'barrel', value: 4 }
    ],
    unlock: 'Save $12,000, earn 300 respect points, and reach aerial level 4.',
    story:
      "After months on the QS circuit, Enzo has transformed. His rankings have shot up, his technique has diversified, and sponsors have taken notice. At a major surf brand event, he's approached by a representative. 'We want to sponsor your Hawaii leg,' they say. 'You've got what it takes.' Enzo knows that Hawaii is where champions are made or broken—Pipeline, Waimea Bay, and contests where careers are defined. With his qualification to the Championship Tour almost secured, he makes the commitment. 'I'm ready,' he says, both to the sponsor and to himself. The ultimate proving ground awaits."
  },

  // Chapter 9: Hawaii
  the_pipeline_proving_ground: {
    key: 'the_pipeline_proving_ground',
    name: 'The Pipeline Proving Ground',
    locationIndex: 1,
    chapterIndex: 9,
    location: 'hawaii' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Pipeline (+30% to barrel riding and fitness).',
    effect: {
      surfSpotUnlocked: 'Pipeline',
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'barrel', value: 1.2 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_front', value: 1.5 }
      ]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'respectTotal', value: 800 },
      { category: 'local_stat', key: 'surfSessionsTotal', value: 40 },
      { category: 'local_stat', key: 'injuriesTotal', value: 1 }
    ],
    unlock: 'Survive your first sessions at Pipeline and earn the respect of locals.',
    story:
      "Enzo arrives in Hawaii to face the legendary Pipeline, the most perfect and dangerous barreling wave in the world. His excitement quickly turns to humility as the power of the reef unleashes its fury. During a heavy session, Enzo takes a devastating wipeout, breaking his board and suffering a painful injury that keeps him out of the water for days. Rather than being discouraged, this experience deepens his respect for the wave. As he recovers and returns to tackle Pipeline again with newfound caution and determination, the locals begin to acknowledge his commitment. The injury was a necessary lesson - at Pipeline, respect isn't given, it's earned through perseverance and humility."
  },
  the_honolua_showdown: {
    key: 'the_honolua_showdown',
    name: 'The Honolua Showdown',
    locationIndex: 2,
    chapterIndex: 9,
    location: 'hawaii' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Honolua Bay (+25% to cutbacks and speed control).',
    effect: {
      surfSpotUnlocked: 'Honolua Bay',
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_front', value: 1.8 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_back', value: 1.8 },
        { type: 'permanent', effectCategory: 'skill', key: 'speed_control', value: 1.7 }
      ]
    },
    unlockRequirements: [
      { category: 'contest', key: 'honolua_bay_open', value: 'semifinals' },
      { category: 'local_stat', key: 'respectTotal', value: 1500 }
    ],
    unlock: 'Earn respect and advance to the quarterfinals at the Honolua Bay event.',
    story:
      "After recovering from his Pipeline injury, Enzo sets his sights on Honolua Bay, a more strategic wave known for its perfect walls and demanding sections. The Honolua Bay Open represents his chance to prove he belongs among the elite. Despite still feeling the lingering effects of his injury, Enzo pushes through pain, focusing on precision rather than power. His calculated approach impresses the judges and fellow competitors. As he advances to the semifinals, Enzo realizes something vital: in Hawaii, technical mastery matters as much as courage. The respect he's earned from both locals and pros has become his most valuable currency, opening doors that would have remained closed to him before."
  },
  waimea_bay_big_wave_dreams: {
    key: 'waimea_bay_big_wave_dreams',
    name: 'Waimea Bay, Big Wave Dreams',
    locationIndex: 3,
    chapterIndex: 9,
    location: 'hawaii' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Waimea Bay (+25% to paddling power and bottom turns).',
    effect: {
      surfSpotUnlocked: 'Waimea Bay',
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'air_reverse', value: 1.5 },
        { type: 'permanent', effectCategory: 'skill', key: 'aerial', value: 1.5 },
        { type: 'permanent', effectCategory: 'skill', key: 'speed_generation', value: 1.5 }
      ]
    },
    unlockRequirements: [{ category: 'contest', key: 'wsa_qualifier_waimea_bay', value: 'semifinals' }],
    unlock: 'Successfully qualify for the semifinals in the WSA Qualifier at Waimea Bay.',
    story:
      "With his confidence growing, Enzo faces his next Hawaiian challenge: Waimea Bay, the birthplace of big wave surfing. The WSA Qualifier there coincides with a massive winter swell, turning the competition into a test of courage as much as skill. Drawing from his Pipeline experience, Enzo approaches these giants with calculated respect. His improved paddling power and precise bottom turns become his greatest weapons as he navigates these mountains of water. Making it to the semifinals marks a turning point - Enzo is no longer just surviving in Hawaii; he's thriving. The qualification puts him one step closer to his ultimate goal: competing for the World Title at Pipeline, where his Hawaiian journey began."
  },
  world_title_at_pipeline: {
    key: 'world_title_at_pipeline',
    name: 'World Title at Pipeline',
    locationIndex: 4,
    chapterIndex: 9,
    location: 'hawaii' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Win the World Title, +50% to all skills, major sponsorships unlocked.',
    effect: {
      locationUnlocked: 'tahiti',
      surfSpotUnlocked: "Teahupo'o",
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_front', value: 1.5 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_back', value: 1.5 },
        { type: 'permanent', effectCategory: 'skill', key: 'floater', value: 1.5 },
        { type: 'permanent', effectCategory: 'skill', key: 'aerial', value: 1.5 },
        { type: 'permanent', effectCategory: 'skill', key: 'air_reverse', value: 1.5 },
        { type: 'permanent', effectCategory: 'skill', key: 'barrel', value: 1.5 }
      ]
    },
    unlockRequirements: [{ category: 'contest', key: 'wsa_championship_pipeline', value: 'won' }],
    unlock: 'Win the World Championship at Pipeline to claim the World Title.',
    story:
      "The moment has arrived - the WSA Championship at Pipeline, with the World Title hanging in the balance. Enzo returns to where his Hawaiian journey began, but now as a contender, not an outsider. In the weeks leading up to the event, he trains relentlessly, rising before dawn for physical conditioning, studying Pipeline's every mood, and even connecting with Hawaiian elders to understand the spiritual significance of this hallowed wave. The preparation is as mental as it is physical. On competition day, Pipeline delivers its legendary conditions - massive, barreling waves that separate the champions from the pretenders. Drawing on every lesson from his journey, Enzo finds himself in sync with the ocean in a way he's never experienced before. In the final heat, with everything on the line, he commits to the wave of the day - a perfect Pipeline barrel that seems impossibly deep. Time slows as he disappears into the tube, the water drawing up around him like a liquid cathedral. When he emerges clean, the beach erupts. The judges award him the highest score of the event, securing not just the win but the World Title. The once-lost Italian kid has completed his transformation - from novice to world champion, from outsider to legend."
  },

  // Chapter 10: Tahiti
  training_for_teahupo_o: {
    key: 'training_for_teahupo_o',
    name: "Training for Teahupo'o",
    locationIndex: 1,
    chapterIndex: 10,
    location: 'tahiti' as LocationEnum,
    type: 'main' as QuestType,
    effectText: "+Unlocks Teahupo'o (+30% to barrel riding and wave reading).",
    effect: {
      surfSpotUnlocked: "Teahupo'o",
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'barrel', value: 1.3 },
        { type: 'permanent', effectCategory: 'skill', key: 'air_reverse', value: 1.5 }
      ]
    },
    unlockRequirements: [{ category: 'local_stat', key: 'wavesTotal', value: 1000 }],
    unlock: "Study and understand the challenging reef formations of Teahupo'o.",
    story:
      "After claiming the World Title, Enzo receives an invitation to the ultimate proving ground: Teahupo'o, Tahiti - home to the heaviest wave on the planet. Despite his success, he approaches this new challenge with humility, knowing Teahupo's unforgiving reef demands complete respect. Enzo spends weeks learning the reef's intricate layout, studying how the swell interacts with underwater formations, and connecting with local watermen who've spent lifetimes decoding its secrets. During his exploration, he experiences both the wave's beauty and brutality, collecting minor reef cuts as badges of his dedication. With each session, he gains deeper understanding of this mystical wave - how it warps from deep water onto the shallow shelf, creating barrels of impossible thickness and power. The locals begin sharing their generational knowledge when they see his respectful approach. Enzo realizes that to truly master Teahupo'o, he must embrace both its physical and spiritual dimensions."
  },
  the_papara_warm_up: {
    key: 'the_papara_warm_up',
    name: 'The Papara Warm Up',
    locationIndex: 2,
    chapterIndex: 10,
    location: 'tahiti' as LocationEnum,
    type: 'main' as QuestType,
    effectText: 'Unlocks Papara (+25% to speed control and turns).',
    effect: {
      surfSpotUnlocked: 'Papara',
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'speed_control', value: 1.25 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_front', value: 1.5 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_back', value: 1.5 }
      ]
    },
    unlockRequirements: [{ category: 'local_stat', key: 'respectTotal', value: 2000 }],
    unlock: 'Earn respect from the locals and master the technical aspects of Papara.',
    story:
      "To prepare for the ultimate challenge at Teahupo'o, Enzo dedicates time at Papara, a slightly more forgiving but still powerful Tahitian wave. Here, among local surfers who've watched champions come and go, Enzo finds an opportunity to refine his technique away from the spotlight. The wave demands precision in a different way - its shifting peaks and sections require perfect speed control and explosive turns. Enzo immerses himself in the rhythm of Tahitian life, rising with the sun, understanding the tide cycles, and building connections with the community. These relationships prove as valuable as his water time - the local knowledge shared over evening meals provides insights no coach could offer. The respect he earns isn't just for his world title, but for his willingness to learn Tahiti's ways. As his comfort at Papara grows, so does his confidence that he might be ready for the final challenge of his journey."
  },
  olympic_gold_at_teahupo_o: {
    key: 'olympic_gold_at_teahupo_o',
    name: "Olympic Gold at Teahupo'o",
    locationIndex: 3,
    chapterIndex: 10,
    location: 'tahiti' as LocationEnum,
    type: 'main' as QuestType,
    effectText: "Become the G.O.A.T. Surfer at Teahupo'o, +50% to all stats, ultimate sponsorships unlocked.",
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'fitness', value: 2 },
        { type: 'permanent', effectCategory: 'skill', key: 'paddling', value: 2 },
        { type: 'permanent', effectCategory: 'skill', key: 'popup', value: 2 },
        { type: 'permanent', effectCategory: 'skill', key: 'whitewater_riding', value: 2 },
        { type: 'permanent', effectCategory: 'skill', key: 'green_wave_riding', value: 2 },
        { type: 'permanent', effectCategory: 'skill', key: 'forecasting_and_observing', value: 2 },
        { type: 'permanent', effectCategory: 'skill', key: 'wave_reading', value: 2 },
        { type: 'permanent', effectCategory: 'skill', key: 'speed_control', value: 2 },
        { type: 'permanent', effectCategory: 'skill', key: 'speed_generation', value: 2 },
        { type: 'permanent', effectCategory: 'skill', key: 'bottom_turn_front', value: 2 },
        { type: 'permanent', effectCategory: 'skill', key: 'bottom_turn_back', value: 2 },
        { type: 'permanent', effectCategory: 'skill', key: 'cutback_front', value: 2 },
        { type: 'permanent', effectCategory: 'skill', key: 'cutback_back', value: 2 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_front', value: 2 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_back', value: 2 },
        { type: 'permanent', effectCategory: 'skill', key: 'floater', value: 2 },
        { type: 'permanent', effectCategory: 'skill', key: 'aerial', value: 2 },
        { type: 'permanent', effectCategory: 'skill', key: 'air_reverse', value: 2 },
        { type: 'permanent', effectCategory: 'skill', key: 'barrel', value: 2 }
      ]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'wavesTotal', value: 3000 },
      { category: 'contest', key: 'teahupoo_olympics', value: 'won' }
    ],
    unlock: "Win the ultimate championship at Teahupo'o and secure your legacy as the G.O.A.T.",
    story:
      "The pinnacle of surfing competition arrives: the Teahupo'o Championships. This event, coinciding with a historic swell, has drawn every elite surfer and media outlet to Tahiti. For Enzo, it represents the final chapter of an incredible journey - from Mediterranean novice to potential Greatest Of All Time. The pressure is immense. Despite being world champion, whispers suggest his title needs validation at surfing's most demanding wave. The morning of the competition, Enzo finds quiet solitude at sunrise, reflecting on every step that brought him here - from that first lesson in Italy to this moment in paradise. When competition begins, Teahupo'o shows its most fearsome face - waves of terrifying size and power that have several competitors abandoning heats. Drawing on everything he's learned, Enzo approaches each wave with a blend of technical precision and intuitive flow that seems to transcend conventional surfing. In the final heat, as his competitors play it safe, Enzo spots a dark mountain on the horizon - a wave of historical proportions. Without hesitation, he commits, paddling toward what could be either career-defining glory or devastating injury. The crowd falls silent as he disappears into what veterans later call 'the barrel of the century' - a cavernous tube that seems to swallow him whole. Seconds stretch into eternity before he emerges, standing tall as the spit launches him into the channel. The beach erupts in celebration. The judges award a perfect score - the first in a championship final in decades. As Enzo raises the trophy, he realizes his journey has come full circle. The ocean that once intimidated him has become his greatest teacher and ally. The Italian kid who once struggled to stand on a foam board has become the G.O.A.T. - but more importantly, he has found his true self in the pursuit."
  },
  // Side quests
  blessing_of_the_mediterranean_breeze: {
    key: 'blessing_of_the_mediterranean_breeze',
    name: 'Blessing of the Mediterranean Breeze',
    locationIndex: 1,
    chapterIndex: 1,
    location: 'mediterranean' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+15% to balance and wave reading',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'popup', value: 1.15 },
        { type: 'permanent', effectCategory: 'skill', key: 'wave_reading', value: 1.15 }
      ]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'gameDaysTotal', value: 10 },
      { category: 'local_stat', key: 'respectTotal', value: 60 },
      { category: 'local_stat', key: 'wavesTotal', value: 120 }
    ],
    unlock: 'Gain 50 respect points in the Mediterranean by riding waves and helping locals.',
    story: 'Embrace the gentle Mediterranean breeze and learn to read its subtle cues for better wave riding.'
  },
  ancient_sea_wisdom: {
    key: 'ancient_sea_wisdom',
    name: 'Ancient Sea Wisdom',
    locationIndex: 2,
    chapterIndex: 1,
    location: 'mediterranean' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+10% to forecasting and observing, +5% to paddling power',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'forecasting_and_observing', value: 1.1 },
        { type: 'permanent', effectCategory: 'skill', key: 'paddling', value: 1.05 }
      ],
      stokeCapUnlocked: STOKE_CAP_TIERS.LEVEL_1
    },
    unlockRequirements: [
      { category: 'level', key: 'forecasting_and_observing', value: 3 },
      { category: 'job', key: 'tour_guide_helper', value: 36 }
    ],
    unlock: 'Predict perfect conditions 3 times and gain respect from local sailors.',
    story: 'Learn from the ancient wisdom of Mediterranean sailors to better predict and observe surf conditions.'
  },
  guardian_of_the_shallows: {
    key: 'guardian_of_the_shallows',
    name: 'Guardian of the Shallows',
    locationIndex: 3,
    chapterIndex: 1,
    location: 'mediterranean' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+20% to whitewater riding and popup',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'whitewater_riding', value: 1.2 },
        { type: 'permanent', effectCategory: 'skill', key: 'popup', value: 1.2 }
      ]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'wipeoutsTotal', value: 36 },
      { category: 'local_stat', key: 'respectTotal', value: 60 }
    ],
    unlock: 'Help beginner surfers and earn 60 respect points.',
    story: 'Become a guardian of the shallows by helping beginners and mastering the art of whitewater riding.'
  },
  fishermans_secret_path: {
    key: 'fishermans_secret_path',
    name: "Fisherman's Secret Path",
    locationIndex: 4,
    chapterIndex: 1,
    location: 'mediterranean' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+10% to paddling power, +10% to bottom turns',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'paddling', value: 1.1 },
        { type: 'permanent', effectCategory: 'skill', key: 'bottom_turn_front', value: 1.1 },
        { type: 'permanent', effectCategory: 'skill', key: 'bottom_turn_back', value: 1.1 }
      ],
      gameSpeedUnlocked: 1.5
    },
    unlockRequirements: [
      { category: 'job', key: 'street_vendor_helper', value: 36 },
      { category: 'local_stat', key: 'respectTotal', value: 60 }
    ],
    unlock: 'Work alongside the fishermen and ride waves near the pier.',
    story:
      'Discover secret surf spots and techniques by working with local fishermen and braving the waves near the pier.'
  },
  spirit_of_the_atlantic_swell: {
    key: 'spirit_of_the_atlantic_swell',
    name: 'Spirit of the Atlantic Swell',
    locationIndex: 1,
    chapterIndex: 2,
    location: 'france' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+15% to paddling power and speed generation',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'paddling', value: 1.15 },
        { type: 'permanent', effectCategory: 'skill', key: 'speed_generation', value: 1.15 }
      ]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'wavesTotal', value: 240 },
      { category: 'local_stat', key: 'wipeoutsTotal', value: 40 },
      { category: 'local_stat', key: 'respectTotal', value: 70 }
    ],
    unlock: 'Paddle out and ride a legendary swell in France while earning 75 respect points.',
    story: 'Embrace the power of the Atlantic swell and learn to harness its energy for better paddling and speed.'
  },
  whisperer_of_tides: {
    key: 'whisperer_of_tides',
    name: 'Whisperer of Tides',
    locationIndex: 2,
    chapterIndex: 2,
    location: 'france' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+20% to forecasting and observing, +10% to wave reading',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'forecasting_and_observing', value: 1.2 },
        { type: 'permanent', effectCategory: 'skill', key: 'wave_reading', value: 1.1 },
        { type: 'permanent', effectCategory: 'currency', key: 'stoke', value: 1.15 }
      ]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'gameDaysTotal', value: 72 },
      { category: 'job', key: 'surf_guide', value: 36 },
      { category: 'level', key: 'forecasting_and_observing', value: 4 }
    ],
    unlock: 'Gain respect for accurately predicting tides and conditions for 3 surf sessions.',
    story: 'Master the art of reading tides and predicting perfect surf conditions in the challenging waters of France.'
  },
  hossegors_blessing: {
    key: 'hossegors_blessing',
    name: "Hossegor's Blessing",
    locationIndex: 3,
    chapterIndex: 2,
    location: 'france' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+15% to turns, +10% to cutbacks',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_front', value: 1.15 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_back', value: 1.15 },
        { type: 'permanent', effectCategory: 'skill', key: 'cutback_front', value: 1.1 },
        { type: 'permanent', effectCategory: 'skill', key: 'cutback_back', value: 1.1 }
      ],
      gameSpeedUnlocked: 2
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'wavesTotal', value: 240 },
      { category: 'local_stat', key: 'respectTotal', value: 100 },
      { category: 'contest', key: 'hossegor_open', value: 'won' }
    ],
    unlock: 'Gain 100 respect points in Hossegor after mastering a difficult wave.',
    story: 'Prove your worth in the challenging waves of Hossegor and earn the respect of the local surfing community.'
  },
  coastal_spirit_connection: {
    key: 'coastal_spirit_connection',
    name: 'Coastal Spirit Connection',
    locationIndex: 4,
    chapterIndex: 2,
    location: 'france' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+10% to turns, +5% to speed control',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_front', value: 1.1 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_back', value: 1.1 },
        { type: 'permanent', effectCategory: 'skill', key: 'speed_control', value: 1.05 }
      ]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'wavesTotal', value: 300 },
      { category: 'local_stat', key: 'respectTotal', value: 120 }
    ],
    unlock: 'Unlock by shaping boards with a local shaper for 20 hours.',
    story: 'Connect with the coastal spirit of France by learning the art of surfboard shaping from local masters.'
  },
  the_lighthouse_whisper: {
    key: 'the_lighthouse_whisper',
    name: 'The Lighthouse Whisper',
    locationIndex: 5,
    chapterIndex: 2,
    location: 'france' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+10% to wave reading, +5% to forecasting',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'wave_reading', value: 1.1 },
        { type: 'permanent', effectCategory: 'skill', key: 'forecasting_and_observing', value: 1.05 }
      ]
    },
    unlockRequirements: [
      { category: 'level', key: 'fitness', value: 4 },
      { category: 'local_stat', key: 'respectTotal', value: 200 }
    ],
    unlock: 'Help locals rescue stranded boats near the lighthouse.',
    story:
      'Learn the secrets of the coast from the lighthouse keeper and use this knowledge to help local sailors and surfers.'
  },
  fados_serenade: {
    key: 'fados_serenade',
    name: "Fado's Serenade",
    locationIndex: 1,
    chapterIndex: 3,
    location: 'portugal' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+20% to balance, +10% to speed control',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'popup', value: 1.2 },
        { type: 'permanent', effectCategory: 'skill', key: 'speed_control', value: 1.1 }
      ],
      stokeCapUnlocked: STOKE_CAP_TIERS.LEVEL_2
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'gameDaysTotal', value: 20 },
      { category: 'local_stat', key: 'respectTotal', value: 120 }
    ],
    unlock: 'Unlock after attending a Fado performance and connecting with the locals.',
    story: 'Immerse yourself in the soulful melodies of Fado and let the rhythm of Portugal flow into your surfing.'
  },
  the_fishermens_blessing: {
    key: 'the_fishermens_blessing',
    name: "The Fishermen's Blessing",
    locationIndex: 2,
    chapterIndex: 3,
    location: 'portugal' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+15% to paddling power and bottom turns',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'paddling', value: 1.15 },
        { type: 'permanent', effectCategory: 'skill', key: 'bottom_turn_front', value: 1.15 },
        { type: 'permanent', effectCategory: 'skill', key: 'bottom_turn_back', value: 1.15 }
      ],
      gameSpeedUnlocked: 3
    },
    unlockRequirements: [
      { category: 'job', key: 'street_vendor_helper', value: 72 },
      { category: 'local_stat', key: 'respectTotal', value: 180 }
    ],
    unlock: 'Help the fishermen during stormy weather and gain their respect.',
    story: 'Brave the stormy seas alongside Portuguese fishermen and learn the ancient wisdom of the ocean.'
  },
  nazares_guardian: {
    key: 'nazares_guardian',
    name: "Nazaré's Guardian",
    locationIndex: 3,
    chapterIndex: 3,
    location: 'portugal' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+25% to barrel riding, +15% to fitness',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'barrel', value: 1.25 },
        { type: 'permanent', effectCategory: 'skill', key: 'fitness', value: 1.15 }
      ]
    },
    unlockRequirements: [
      { category: 'job', key: 'big_wave_surf_guide', value: 50 },
      { category: 'local_stat', key: 'wavesTotal', value: 360 },
      { category: 'local_stat', key: 'respectTotal', value: 250 }
    ],
    unlock: 'Gain respect from surviving a big wave at Nazaré and ride at least 50 waves.',
    story: 'Face the monstrous waves of Nazaré and earn your place as a respected big wave surfer.'
  },
  spirit_of_the_western_atlantic: {
    key: 'spirit_of_the_western_atlantic',
    name: 'Spirit of the Western Atlantic',
    locationIndex: 4,
    chapterIndex: 3,
    location: 'portugal' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+10% to speed generation, +10% to top turns',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'speed_generation', value: 1.1 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_front', value: 1.1 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_back', value: 1.1 }
      ]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'wavesTotal', value: 360 },
      { category: 'local_stat', key: 'wipeoutsTotal', value: 30 },
      { category: 'local_stat', key: 'respectTotal', value: 300 }
    ],
    unlock: 'Unlock by spending a week riding the heaviest waves at Peniche.',
    story: 'Embrace the raw power of the Western Atlantic and master the waves of Peniche.'
  },
  spirit_of_the_great_white: {
    key: 'spirit_of_the_great_white',
    name: 'Spirit of the Great White',
    locationIndex: 1,
    chapterIndex: 4,
    location: 'south_africa' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+25% to fitness, +15% to speed generation',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'fitness', value: 1.25 },
        { type: 'permanent', effectCategory: 'skill', key: 'speed_generation', value: 1.15 }
      ]
    },
    unlockRequirements: [
      { category: 'job', key: 'shark_cage_dive_guide', value: 110 },
      { category: 'local_stat', key: 'respectTotal', value: 150 }
    ],
    unlock: 'Guide tourists safely through shark-infested waters and earn 100 respect points.',
    story:
      'Face your fears and gain the respect of the ocean by guiding others through the domain of the great white sharks.'
  },
  voice_of_the_zulu_seas: {
    key: 'voice_of_the_zulu_seas',
    name: 'Voice of the Zulu Seas',
    locationIndex: 2,
    chapterIndex: 4,
    location: 'south_africa' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+20% to wave reading, +10% to top turns',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'wave_reading', value: 1.2 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_front', value: 1.1 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_back', value: 1.1 }
      ]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'gameDaysTotal', value: 20 },
      { category: 'local_stat', key: 'wavesTotal', value: 400 },
      { category: 'local_stat', key: 'respectTotal', value: 200 }
    ],
    unlock: "Learn the Zulu people's ocean stories and connect with their cultural heritage.",
    story:
      'Immerse yourself in the rich cultural heritage of the Zulu people and gain a deeper understanding of the ocean through their ancient wisdom.'
  },
  connection_with_the_dolphins: {
    key: 'connection_with_the_dolphins',
    name: 'Connection with the Dolphins',
    locationIndex: 3,
    chapterIndex: 4,
    location: 'south_africa' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+15% to balance, +15% to paddling power',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'popup', value: 1.15 },
        { type: 'permanent', effectCategory: 'skill', key: 'paddling', value: 1.15 },
        { type: 'permanent', effectCategory: 'currency', key: 'stoke', value: 1.2 }
      ]
    },
    unlockRequirements: [{ category: 'job', key: 'safari_surf_tour_operator', value: 480 }],
    unlock: 'Earn respect after having a surf session with dolphins and impressing locals.',
    story:
      'Experience the magic of surfing alongside dolphins and learn to move through the water with their grace and agility.'
  },
  soul_of_the_boulders: {
    key: 'soul_of_the_boulders',
    name: 'Soul of the Boulders',
    locationIndex: 4,
    chapterIndex: 4,
    location: 'south_africa' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+20% to barrel riding, +10% to fitness',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'barrel', value: 1.2 },
        { type: 'permanent', effectCategory: 'skill', key: 'fitness', value: 1.1 }
      ],
      gameSpeedUnlocked: 5
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'wavesTotal', value: 800 },
      { category: 'local_stat', key: 'respectTotal', value: 300 }
    ],
    unlock: "Unlock after surviving wipeouts near Cape Town's rocky shores.",
    story:
      'Challenge yourself against the unforgiving rocky breaks of Cape Town and emerge stronger, both physically and mentally.'
  },
  jeffreys_bay_guardian: {
    key: 'jeffreys_bay_guardian',
    name: 'Jeffreys Bay Guardian',
    locationIndex: 5,
    chapterIndex: 4,
    location: 'south_africa' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+25% to cutbacks, +15% to bottom turns',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'cutback_front', value: 1.25 },
        { type: 'permanent', effectCategory: 'skill', key: 'cutback_back', value: 1.25 },
        { type: 'permanent', effectCategory: 'skill', key: 'bottom_turn_front', value: 1.15 },
        { type: 'permanent', effectCategory: 'skill', key: 'bottom_turn_back', value: 1.15 }
      ]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'wavesTotal', value: 1200 },
      { category: 'local_stat', key: 'respectTotal', value: 480 }
    ],
    unlock: 'Gain respect from the locals after mastering the waves at Jeffreys Bay.',
    story: 'Prove your worth on the legendary waves of Jeffreys Bay and become a true guardian of this surfing mecca.'
  },
  seals_grace: {
    key: 'seals_grace',
    name: "Seal's Grace",
    locationIndex: 1,
    chapterIndex: 5,
    location: 'california' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+20% to turns, +10% to paddling power',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_front', value: 1.2 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_back', value: 1.2 },
        { type: 'permanent', effectCategory: 'skill', key: 'paddling', value: 1.1 }
      ],
      stokeCapUnlocked: STOKE_CAP_TIERS.LEVEL_3
    },
    unlockRequirements: [
      { category: 'job', key: 'beach_lifeguard', value: 280 },
      { category: 'level', key: 'fitness', value: 4 }
    ],
    unlock: 'Rescue a trapped seal and earn 80 respect points.',
    story: 'Show your compassion by rescuing a seal in distress, and let its grace in the water inspire your surfing.'
  },
  spirit_of_malibu: {
    key: 'spirit_of_malibu',
    name: 'Spirit of Malibu',
    locationIndex: 2,
    chapterIndex: 5,
    location: 'california' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+15% to cutbacks, +10% to speed generation',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'cutback_front', value: 1.15 },
        { type: 'permanent', effectCategory: 'skill', key: 'cutback_back', value: 1.15 },
        { type: 'permanent', effectCategory: 'skill', key: 'speed_generation', value: 1.1 }
      ]
    },
    unlockRequirements: [
      { category: 'job', key: 'surf_camp_coordinator', value: 400 },
      { category: 'local_stat', key: 'respectTotal', value: 250 }
    ],
    unlock: 'Gain the respect of the locals by mastering a wave at Malibu.',
    story: 'Embrace the laid-back yet competitive spirit of Malibu and make your mark on this iconic Californian break.'
  },
  sunset_glow: {
    key: 'sunset_glow',
    name: 'Sunset Glow',
    locationIndex: 3,
    chapterIndex: 5,
    location: 'california' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+25% to fitness, +15% to aerials',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'fitness', value: 1.75 },
        { type: 'permanent', effectCategory: 'skill', key: 'aerial', value: 1.45 }
      ],
      gameSpeedUnlocked: 7
    },
    unlockRequirements: [
      { category: 'job', key: 'pro_surf_instructor', value: 800 },
      { category: 'level', key: 'fitness', value: 5 }
    ],
    unlock: 'Impress the locals by consistently surfing at sunset and mastering the breaks.',
    story: 'Become one with the golden hour, mastering the art of surfing as the sun dips below the horizon.'
  },
  pacific_light_connection: {
    key: 'pacific_light_connection',
    name: 'Pacific Light Connection',
    locationIndex: 4,
    chapterIndex: 5,
    location: 'california' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+10% to speed generation, +15% to top turns',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'speed_generation', value: 1.4 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_front', value: 1.45 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_back', value: 1.45 }
      ]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'wavesTotal', value: 2000 },
      { category: 'local_stat', key: 'respectTotal', value: 400 }
    ],
    unlock: "Unlock after surfing during a bioluminescent night and earning the locals' admiration.",
    story: 'Experience the magic of surfing amidst bioluminescent waves, connecting with the ocean in a whole new way.'
  },
  blessing_of_the_great_barrier_reef: {
    key: 'blessing_of_the_great_barrier_reef',
    name: 'Blessing of the Great Barrier Reef',
    locationIndex: 1,
    chapterIndex: 6,
    location: 'australia_east_coast' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+25% to paddling power, +15% to balance',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'paddling', value: 1.75 },
        { type: 'permanent', effectCategory: 'skill', key: 'popup', value: 1.45 }
      ]
    },
    unlockRequirements: [
      { category: 'job', key: 'surfboard_shaping_apprentice', value: 300 },
      { category: 'local_stat', key: 'respectTotal', value: 300 }
    ],
    unlock: 'Help with reef conservation and ride waves near the reef.',
    story:
      'Contribute to the preservation of the Great Barrier Reef and gain a deeper connection with the ocean ecosystem.'
  },
  spirit_of_the_kangaroo: {
    key: 'spirit_of_the_kangaroo',
    name: 'Spirit of the Kangaroo',
    locationIndex: 2,
    chapterIndex: 6,
    location: 'australia_east_coast' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+20% to turns and cutbacks',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_front', value: 1.6 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_back', value: 1.6 },
        { type: 'permanent', effectCategory: 'skill', key: 'cutback_front', value: 1.6 },
        { type: 'permanent', effectCategory: 'skill', key: 'cutback_back', value: 1.6 }
      ],
      gameSpeedUnlocked: 10
    },
    unlockRequirements: [
      { category: 'job', key: 'surfboard_shaper', value: 500 },
      { category: 'level', key: 'fitness', value: 5 }
    ],
    unlock: 'Gain the respect of Aboriginal elders by learning their oceanic wisdom.',
    story: 'Immerse yourself in Aboriginal culture and apply their ancient wisdom to your surfing technique.'
  },
  whispers_of_byron_bay: {
    key: 'whispers_of_byron_bay',
    name: 'Whispers of Byron Bay',
    locationIndex: 3,
    chapterIndex: 6,
    location: 'australia_east_coast' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+15% to wave reading, +10% to top turns',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'wave_reading', value: 1.45 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_front', value: 1.4 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_back', value: 1.4 },
        { type: 'permanent', effectCategory: 'currency', key: 'stoke', value: 1.25 }
      ]
    },
    unlockRequirements: [
      { category: 'job', key: 'pro_surfboard_designer', value: 800 },
      { category: 'local_stat', key: 'respectTotal', value: 350 }
    ],
    unlock: 'Gain respect by connecting with the surf mystics in Byron Bay.',
    story: 'Tap into the spiritual energy of Byron Bay and let it guide your surfing to new heights.'
  },
  soul_of_the_outback_coast: {
    key: 'soul_of_the_outback_coast',
    name: 'Soul of the Outback Coast',
    locationIndex: 4,
    chapterIndex: 6,
    location: 'australia_east_coast' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+10% to balance, +15% to paddling power',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'popup', value: 1.4 },
        { type: 'permanent', effectCategory: 'skill', key: 'paddling', value: 1.45 }
      ]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'wipeoutsTotal', value: 100 },
      { category: 'local_stat', key: 'wavesTotal', value: 2000 },
      { category: 'local_stat', key: 'respectTotal', value: 600 }
    ],
    unlock: 'Survive an expedition along the remote Outback coastline.',
    story: 'Challenge yourself against the rugged Outback coast and emerge as a more resilient surfer.'
  },
  gift_of_the_monsoon: {
    key: 'gift_of_the_monsoon',
    name: 'Gift of the Monsoon',
    locationIndex: 1,
    chapterIndex: 7,
    location: 'indonesia' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+20% to barrel riding, +15% to speed control',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'barrel', value: 1.6 },
        { type: 'permanent', effectCategory: 'skill', key: 'speed_control', value: 1.45 }
      ]
    },
    unlockRequirements: [{ category: 'job', key: 'diving_instructor', value: 250 }],
    unlock: 'Ride waves during a monsoon without wiping out.',
    story: 'Embrace the power of the monsoon and learn to harness its energy in your surfing.'
  },
  blessing_of_bali: {
    key: 'blessing_of_bali',
    name: 'Blessing of Bali',
    locationIndex: 2,
    chapterIndex: 7,
    location: 'indonesia' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+25% to balance, +20% to turns',
    effect: {
      stokeCapUnlocked: STOKE_CAP_TIERS.LEVEL_4,
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'popup', value: 1.75 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_front', value: 1.6 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_back', value: 1.6 }
      ]
    },
    unlockRequirements: [
      { category: 'job', key: 'surf_and_yoga_retreat_manager', value: 600 },
      { category: 'local_stat', key: 'respectTotal', value: 450 }
    ],
    unlock: 'Earn respect after participating in a Balinese surf ceremony.',
    story: 'Immerse yourself in Balinese culture and let its spiritual energy enhance your surfing abilities.'
  },
  mystical_meditation: {
    key: 'mystical_meditation',
    name: 'Mystical Meditation',
    locationIndex: 3,
    chapterIndex: 7,
    location: 'indonesia' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+20% to fitness and stamina',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'fitness', value: 1.6 },
        { type: 'permanent', effectCategory: 'skill', key: 'paddling', value: 1.6 }
      ]
    },
    unlockRequirements: [
      { category: 'job', key: 'private_surf_guide_high_end_tourists', value: 1000 },
      { category: 'local_stat', key: 'respectTotal', value: 500 }
    ],
    unlock: 'Master the 3-day silent meditation retreat with local monks.',
    story: 'Find inner peace and mental strength through meditation, enhancing your overall surfing performance.'
  },
  spirit_of_uluwatu: {
    key: 'spirit_of_uluwatu',
    name: 'Spirit of Uluwatu',
    locationIndex: 4,
    chapterIndex: 7,
    location: 'indonesia' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+15% to paddling power, +20% to barrel riding',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'paddling', value: 1.45 },
        { type: 'permanent', effectCategory: 'skill', key: 'barrel', value: 1.6 }
      ],
      gameSpeedUnlocked: 15
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'wavesTotal', value: 2500 },
      { category: 'local_stat', key: 'wipeoutsTotal', value: 100 },
      { category: 'local_stat', key: 'respectTotal', value: 800 }
    ],
    unlock: 'Unlock after mastering the dangerous Uluwatu barrels.',
    story: 'Conquer the legendary barrels of Uluwatu and become one with its powerful waves.'
  },
  spirit_of_the_outback: {
    key: 'spirit_of_the_outback',
    name: 'Spirit of the Outback',
    locationIndex: 1,
    chapterIndex: 8,
    location: 'australia_west_coast' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+30% to fitness, +15% to paddling power',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'fitness', value: 1.9 },
        { type: 'permanent', effectCategory: 'skill', key: 'paddling', value: 1.45 },
        { type: 'permanent', effectCategory: 'currency', key: 'stoke', value: 1.3 }
      ]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'gameDaysTotal', value: 30 },
      { category: 'local_stat', key: 'wavesTotal', value: 1200 },
      { category: 'local_stat', key: 'respectTotal', value: 500 }
    ],
    unlock: 'Complete a solo surf expedition along the Outback coastline.',
    story: 'Embark on a challenging solo expedition and discover the raw power of the Outback coast.'
  },
  whale_song_whisperer: {
    key: 'whale_song_whisperer',
    name: 'Whale Song Whisperer',
    locationIndex: 2,
    chapterIndex: 8,
    location: 'australia_west_coast' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+20% to wave reading, +15% to barrel riding',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'wave_reading', value: 1.6 },
        { type: 'permanent', effectCategory: 'skill', key: 'barrel', value: 1.45 }
      ],
      gameSpeedUnlocked: 20
    },
    unlockRequirements: [
      { category: 'level', key: 'forecasting_and_observing', value: 5 },
      { category: 'local_stat', key: 'wavesTotal', value: 1800 },
      { category: 'local_stat', key: 'respectTotal', value: 650 }
    ],
    unlock: 'Surf alongside migrating whales and gain respect from the locals.',
    story: 'Experience the awe-inspiring presence of whales and let their energy guide your surfing.'
  },
  sunset_serpent_spirit: {
    key: 'sunset_serpent_spirit',
    name: 'Sunset Serpent Spirit',
    locationIndex: 3,
    chapterIndex: 8,
    location: 'australia_west_coast' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+15% to turns, +10% to top turns',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_front', value: 1.45 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_back', value: 1.45 },
        { type: 'permanent', effectCategory: 'skill', key: 'cutback_front', value: 1.4 },
        { type: 'permanent', effectCategory: 'skill', key: 'cutback_back', value: 1.4 }
      ]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'wavesTotal', value: 2400 },
      { category: 'local_stat', key: 'respectTotal', value: 800 }
    ],
    unlock: 'Unlock by participating in a sunset ceremony with the Aboriginal community.',
    story: 'Connect with the ancient spirits of the land and sea through a sacred Aboriginal ceremony.'
  },
  aloha_spirit: {
    key: 'aloha_spirit',
    name: 'Aloha Spirit',
    locationIndex: 1,
    chapterIndex: 9,
    location: 'hawaii' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+25% to fitness, +15% to paddling power',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'fitness', value: 1.75 },
        { type: 'permanent', effectCategory: 'skill', key: 'paddling', value: 1.45 }
      ]
    },
    unlockRequirements: [
      { category: 'job', key: 'surf_travel_vlogger', value: 300 },
      { category: 'local_stat', key: 'respectTotal', value: 1000 }
    ],
    unlock: 'Embrace the Aloha spirit by spending time with the local community.',
    story: 'Immerse yourself in the Hawaiian culture and let the Aloha spirit guide your surfing journey.'
  },
  ocean_eyes_of_pele: {
    key: 'ocean_eyes_of_pele',
    name: 'Ocean Eyes of Pele',
    locationIndex: 2,
    chapterIndex: 9,
    location: 'hawaii' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+30% to wave reading, +20% to forecasting',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'wave_reading', value: 1.9 },
        { type: 'permanent', effectCategory: 'skill', key: 'forecasting_and_observing', value: 1.6 }
      ],
      gameSpeedUnlocked: 30
    },
    unlockRequirements: [
      { category: 'job', key: 'pro_level_vlogger', value: 600 },
      { category: 'local_stat', key: 'respectTotal', value: 1800 }
    ],
    unlock: 'Unlock after paying homage to the Hawaiian goddess Pele.',
    story: 'Gain the blessing of Pele and develop an almost supernatural ability to read the ocean.'
  },
  sacred_turtle_guardian: {
    key: 'sacred_turtle_guardian',
    name: 'Sacred Turtle Guardian',
    locationIndex: 3,
    chapterIndex: 9,
    location: 'hawaii' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+20% to barrel riding, +15% to balance',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'barrel', value: 1.6 },
        { type: 'permanent', effectCategory: 'skill', key: 'popup', value: 1.45 }
      ],
      stokeCapUnlocked: STOKE_CAP_TIERS.LEVEL_5
    },
    unlockRequirements: [
      { category: 'job', key: 'major_surf_brand', value: 1200 },
      { category: 'local_stat', key: 'respectTotal', value: 2000 }
    ],
    unlock: 'Gain respect by protecting a turtle sanctuary while surfing.',
    story: 'Become a guardian of the sacred sea turtles and let their ancient wisdom guide your surfing.'
  },
  makaha_warriors_path: {
    key: 'makaha_warriors_path',
    name: "Makaha Warrior's Path",
    locationIndex: 4,
    chapterIndex: 9,
    location: 'hawaii' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+20% to top turns, +15% to bottom turns',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_front', value: 1.6 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_back', value: 1.6 },
        { type: 'permanent', effectCategory: 'skill', key: 'bottom_turn_front', value: 1.45 },
        { type: 'permanent', effectCategory: 'skill', key: 'bottom_turn_back', value: 1.45 }
      ]
    },
    unlockRequirements: [
      { category: 'job', key: 'surf_travel_vlogger', value: 300 },
      { category: 'local_stat', key: 'respectTotal', value: 1800 }
    ],
    unlock: 'Unlock by mastering the challenging waves at Makaha.',
    story: 'Follow in the footsteps of Hawaiian surf legends and conquer the powerful waves of Makaha.'
  },
  teahupo_os_embrace: {
    key: 'teahupo_os_embrace',
    name: "Teahupo'o's Embrace",
    locationIndex: 1,
    chapterIndex: 10,
    location: 'tahiti' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+35% to barrel riding, +15% to speed generation',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'barrel', value: 2.05 },
        { type: 'permanent', effectCategory: 'skill', key: 'speed_generation', value: 1.45 },
        { type: 'permanent', effectCategory: 'currency', key: 'stoke', value: 1.5 }
      ]
    },
    unlockRequirements: [
      { category: 'job', key: 'online_surf_coaching_platform', value: 600 },
      { category: 'local_stat', key: 'respectTotal', value: 2200 }
    ],
    unlock: "Ride a massive wave at Teahupo'o and survive its challenge.",
    story: "Face the legendary power of Teahupo'o and earn your place among the world's elite surfers."
  },
  tiki_spirit_blessing: {
    key: 'tiki_spirit_blessing',
    name: 'Tiki Spirit Blessing',
    locationIndex: 2,
    chapterIndex: 10,
    location: 'tahiti' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+25% to balance, +20% to turns',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'popup', value: 1.75 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_front', value: 1.6 },
        { type: 'permanent', effectCategory: 'skill', key: 'top_turn_back', value: 1.6 }
      ]
    },
    unlockRequirements: [
      { category: 'job', key: 'surf_camp_franchise', value: 800 },
      { category: 'local_stat', key: 'respectTotal', value: 2500 }
    ],
    unlock: 'Participate in a Tiki ceremony and earn the respect of the Tahitian locals.',
    story: 'Receive the blessing of the Tiki spirits and channel their power into your surfing.'
  },
  breath_of_the_island_gods: {
    key: 'breath_of_the_island_gods',
    name: 'Breath of the Island Gods',
    locationIndex: 3,
    chapterIndex: 10,
    location: 'tahiti' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+30% to fitness, +20% to stamina',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'fitness', value: 1.9 },
        { type: 'permanent', effectCategory: 'skill', key: 'paddling', value: 1.6 }
      ],
      gameSpeedUnlocked: 50
    },
    unlockRequirements: [
      { category: 'job', key: 'wave_pool_business', value: 1000 },
      { category: 'local_stat', key: 'wavesTotal', value: 5000 },
      { category: 'local_stat', key: 'respectTotal', value: 2800 }
    ],
    unlock: 'Master a traditional breath-hold diving technique.',
    story: 'Learn the ancient Polynesian art of breath-holding and apply it to your big wave surfing.'
  },
  pearl_of_the_pacific: {
    key: 'pearl_of_the_pacific',
    name: 'Pearl of the Pacific',
    locationIndex: 4,
    chapterIndex: 10,
    location: 'tahiti' as LocationEnum,
    type: 'side' as QuestType,
    effectText: '+20% to aerials, +15% to barrel riding',
    effect: {
      multipliers: [
        { type: 'permanent', effectCategory: 'skill', key: 'aerial', value: 1.6 },
        { type: 'permanent', effectCategory: 'skill', key: 'barrel', value: 1.45 }
      ]
    },
    unlockRequirements: [
      { category: 'local_stat', key: 'wavesTotal', value: 6500 },
      { category: 'local_stat', key: 'respectTotal', value: 4000 }
    ],
    unlock: 'Gain the respect of the locals after a successful pearl diving expedition.',
    story: 'Discover the hidden treasures of the Pacific and incorporate their beauty into your surfing style.'
  }
}

const CHAPTERS: QuestChapter[] = [
  {
    chapterIndex: 1,
    key: 'mediterranean',
    name: 'Mediterranean',
    description:
      "Enzo's introduction to surfing and humble beginnings. He's unskilled and learning through persistence, saving money, and his first small victories.",
    quests: [
      'the_first_taste_of_saltwater',
      'first_steps_to_independence',
      'lessons_from_the_locals',
      'the_mediterranean_farewell'
    ],
    sideQuests: [
      'blessing_of_the_mediterranean_breeze',
      'ancient_sea_wisdom',
      'guardian_of_the_shallows',
      'fishermans_secret_path'
    ]
  },
  {
    chapterIndex: 2,
    key: 'france',
    name: 'France',
    description:
      'Enzo confronts tougher waves and financial struggles, meets new friends like Sophie, and faces antagonists like Jacques. His first major test is the waves of Hossegor.',
    quests: ['the_challenge_of_hossegor', 'broke_in_biarritz', 'rivalry_at_la_graviere', 'the_journey_continues'],
    sideQuests: [
      'spirit_of_the_atlantic_swell',
      'whisperer_of_tides',
      'hossegors_blessing',
      'coastal_spirit_connection',
      'the_lighthouse_whisper'
    ]
  }
]
const FULL_GAME_CHAPTERS: QuestChapter[] = [
  {
    chapterIndex: 3,
    key: 'portugal',
    name: 'Portugal',
    description:
      "Enzo's journey deepens in Portugal, where he begins to master harder waves like Nazaré and faces spiritual challenges alongside physical ones. This chapter focuses on personal growth, deeper friendships, and the challenges of learning humility in the face of powerful waves.",
    quests: ['peniches_shifting_sands', 'facing_nazares_fury', 'the_mysterious_traveler', 'a_leap_of_faith'],
    sideQuests: ['nazares_guardian', 'fados_serenade', 'the_fishermens_blessing', 'spirit_of_the_western_atlantic']
  },

  {
    chapterIndex: 4,
    key: 'south_africa',
    name: 'South Africa',
    description:
      "Enzo's journey intensifies in South Africa, where the waves are fierce, and he must confront fear, physical limits, and moments of self-doubt. No clear villain here—just the raw power of the ocean as his greatest test.",
    quests: [
      'teaching_italian_to_shark_diver',
      'the_call_of_jeffreys_bay',
      'conquering_fear',
      'the_long_paddle_to_durban',
      'the_soul_of_south_africa'
    ],
    sideQuests: [
      'spirit_of_the_great_white',
      'voice_of_the_zulu_seas',
      'connection_with_the_dolphins',
      'soul_of_the_boulders',
      'jeffreys_bay_guardian'
    ]
  },

  {
    chapterIndex: 5,
    key: 'california',
    name: 'California',
    description:
      "Enzo reaches California, where he encounters new waves, new friends, and his first experiences with local competitions. Although he can enter small local contests, he isn't ready to win yet. This chapter is about humility, learning from failure, and building his skills.",
    quests: ['the_california_dream', 'a_cold_reality', 'first_competition_at_huntington', 'chasing_warmer_waters'],
    sideQuests: ['seals_grace', 'spirit_of_malibu', 'sunset_glow', 'pacific_light_connection']
  },
  {
    chapterIndex: 6,
    key: 'australia_east_coast',
    name: 'Australia East Coast',
    description:
      "Enzo continues to build his skills and enters more local competitions, but he's still learning and finishes in the middle of the pack. He meets new allies and mentors who push him to develop the more technical aspects of his surfing.",
    quests: [
      'byron_bays_calm_before_the_storm',
      'snapper_rocks_fury',
      'battling_through_the_gold_coast',
      'the_warm_australian_goodbye'
    ],
    sideQuests: [
      'blessing_of_the_great_barrier_reef',
      'spirit_of_the_kangaroo',
      'whispers_of_byron_bay',
      'soul_of_the_outback_coast'
    ]
  },
  {
    chapterIndex: 7,
    key: 'indonesia',
    name: 'Indonesia',
    description:
      "Enzo's journey in Indonesia is all about chasing his first barrel. He faces frustration and wipeouts as he tries to conquer the famous reef breaks of Uluwatu and Padang Padang, but his determination pushes him forward.",
    quests: [
      'uluwatus_relentless_waves',
      'the_frustration_of_padang_padang',
      'mentawai_islands_the_first_barrel',
      'the_next_horizon'
    ],
    sideQuests: ['gift_of_the_monsoon', 'blessing_of_bali', 'mystical_meditation', 'spirit_of_uluwatu']
  },
  {
    chapterIndex: 8,
    key: 'australia_west_coast',
    name: 'Australia West Coast',
    description:
      'In Western Australia, Enzo enters the Qualification Series (QS), a critical step toward becoming a professional surfer on the Championship Tour (CT). He faces intense competition, learns the mental game of competitive surfing, and finally starts earning recognition from sponsors.',
    quests: ['entering_the_qs', 'the_aerial_revolution', 'margaret_river_qualification', 'breakthrough_in_the_west'],
    sideQuests: ['spirit_of_the_outback', 'whale_song_whisperer', 'sunset_serpent_spirit']
  },
  {
    chapterIndex: 9,
    key: 'hawaii',
    name: 'Hawaii',
    description:
      'Enzo is now on the Championship Tour (CT), competing with the best surfers in the world. He enters the World Title race, surfing iconic Hawaiian breaks like Pipeline and Waimea Bay. After intense competition and near misses, Enzo wins the World Title in a dramatic finale.',
    quests: [
      'the_pipeline_proving_ground',
      'the_honolua_showdown',
      'waimea_bay_big_wave_dreams',
      'world_title_at_pipeline'
    ],
    sideQuests: []
  },
  {
    chapterIndex: 10,
    key: 'tahiti',
    name: 'Tahiti and the Olympics',
    description:
      "The Olympics in Tahiti mark the ultimate test for Enzo, who represents his country on the world stage. The infamous wave at Teahupo'o is the venue, and the stakes are higher than ever. In a thrilling final, Enzo competes for Olympic gold at the heaviest wave on the planet.",
    quests: ['training_for_teahupo_o', 'the_papara_warm_up', 'olympic_gold_at_teahupo_o'],
    sideQuests: ['teahupo_os_embrace', 'tiki_spirit_blessing', 'breath_of_the_island_gods', 'pearl_of_the_pacific']
  }
]

const OTHER_CHAPTERS: QuestChapter[] = []

export function getAvailableChapters(): QuestChapter[] {
  if (isDemo()) {
    return CHAPTERS
  }
  return [...CHAPTERS, ...FULL_GAME_CHAPTERS]
}
